// Author TRUST — who opened this issue or pull request, as ONE closed answer.
//
// Before this module the question had three answers: the babysit ask-before-acting
// classifier (`entities/babysit/policy.ts`), the issue-answer lane's project-member set
// (`apps/worker` `gh-issue-answer.ts`) and the issue-intake lane's bare bot check
// (`gh-issue-intake.ts`). Each re-read GitHub's `author_association` its own way, so a
// fix to one (an unknown association failing closed) never reached the others. Every
// reader now asks {@link authorTrust}; `scripts/one-impl-concerns.ts` rejects a second.
//
// The rungs, in the order they are decided:
//
//   - `platform`: our own App's login (`<GITHUB_APP_SLUG>[bot]`). It is env-derived —
//     `@developerz/github` `requireOurBotLogin`, which refuses to default it (#1819) — so
//     the CALLER passes it; domain reads no env.
//   - `bot`: {@link looksLikeBot} — `type: 'Bot'`, a `*[bot]` login, or a known bot login.
//   - `project`: OWNER / MEMBER / COLLABORATOR.
//   - `contributor`: CONTRIBUTOR — GitHub's "has previously committed to the repository".
//   - `outside`: MANNEQUIN — a placeholder for an imported, unclaimed identity.
//   - `first_time`: FIRST_TIME_CONTRIBUTOR / FIRST_TIMER / NONE, and ANY association this
//     module does not know, blank or absent included. FAILS CLOSED (plan 101 PR1): a
//     human whose standing GitHub did not report may be a first-timer, and an unreadable
//     association must never be read as more trust than the lowest human rung.

import { z } from 'zod';
import { looksLikeBot } from '../entities/babysit/github-mappers';
import type { ProjectMembership } from './auto-merge-scope';

/** Every answer {@link authorTrust} can give, ordered from most to least trusted human. */
export const AUTHOR_TRUSTS = [
  'platform',
  'bot',
  'project',
  'contributor',
  'outside',
  'first_time',
] as const;

/** The closed set of author-trust answers. */
export type AuthorTrust = (typeof AUTHOR_TRUSTS)[number];

/**
 * Every rung a BOX can name: `platform` is not a wire value — the box holds no
 * platform identity, so its own App reads as `bot` (the conservative side) and
 * `platform` cannot come back. Moved here from the runner's `pr-screen.ts` (#6339):
 * the mothership reads the same rung off the stored `review_pr_screen` row, and one
 * vocabulary must not be spelled twice.
 */
export type BoxAuthorTrust = Exclude<AuthorTrust, 'platform'>;

/**
 * GitHub `author_association` → trust, for every value GitHub documents
 * (https://docs.github.com/en/graphql/reference/enums#commentauthorassociation).
 * `NONE` is a first-timer: GitHub reports it for an author with no prior commit, issue or
 * PR on the repo.
 */
const ASSOCIATION_TRUST = {
  OWNER: 'project',
  MEMBER: 'project',
  COLLABORATOR: 'project',
  CONTRIBUTOR: 'contributor',
  MANNEQUIN: 'outside',
  FIRST_TIME_CONTRIBUTOR: 'first_time',
  FIRST_TIMER: 'first_time',
  NONE: 'first_time',
} as const satisfies Record<string, Exclude<AuthorTrust, 'platform' | 'bot'>>;

export type AuthorTrustInput = {
  readonly login: string;
  /** GitHub's `user.type` (`'User'`, `'Bot'`, `'Organization'`), when the payload carried it. */
  readonly type?: string | undefined;
  /** GitHub's `author_association`, raw; `null` when the payload carried none. */
  readonly association: string | null | undefined;
  /**
   * Our own App's login (`requireOurBotLogin()`), or `null` when the caller's seat holds
   * no platform identity (a blank string reads the same). `null` is explicit, never a default: our App then reads as
   * `bot` — still never a human rung — which is the conservative side of the two.
   */
  readonly ourBotLogin: string | null;
};

/** Who wrote it, as one closed answer. Pure; see the module header for the rungs. */
export function authorTrust(input: AuthorTrustInput): AuthorTrust {
  // A blank login is an author GitHub did not name (`guardedMerge` reads a null `pr.user`
  // as ''). It never matches `platform` (a blank `ourBotLogin` never matches either); a
  // `type: 'Bot'` still reads as bot, so issue intake keeps refusing it; otherwise it
  // fails closed to `first_time` before the association table can grant it anything.
  const login = input.login.trim().toLowerCase();
  const ours = input.ourBotLogin?.trim().toLowerCase() ?? '';
  if (login !== '' && ours !== '' && login === ours) return 'platform';
  if (looksLikeBot({ login: input.login, type: input.type })) return 'bot';
  if (login === '') return 'first_time';
  const association = input.association?.trim().toUpperCase() ?? '';
  return Object.hasOwn(ASSOCIATION_TRUST, association)
    ? ASSOCIATION_TRUST[association as keyof typeof ASSOCIATION_TRUST]
    : 'first_time';
}

/**
 * {@link authorTrust}'s answer with the author's REPO PERMISSION folded in (#6359). GitHub
 * reports an org's private member to our App as CONTRIBUTOR, so the association grade
 * cannot see one. ONLY a definite `member` lifts a human rung to `project`; `not_member`,
 * `unknown` and `null` (not read) keep the association grade — never fail open on trust —
 * and `platform` / `bot` never move.
 */
export function trustWithMembership(
  trust: AuthorTrust,
  membership: ProjectMembership | null | undefined,
): AuthorTrust {
  return membership === 'member' && trust !== 'platform' && trust !== 'bot' ? 'project' : trust;
}

/**
 * The associations {@link authorTrust} answers `project` for, as words for a SENTENCE
 * ("OWNER, MEMBER or COLLABORATOR") — read off the table above, so a message that tells
 * a human who counts as a project member cannot name a different set than the one
 * `authorTrust` grades. Prose only: the trust question is always `authorTrust`'s.
 */
export function projectAssociationsPhrase(): string {
  const names = Object.entries(ASSOCIATION_TRUST)
    .filter(([, trust]) => trust === 'project')
    .map(([association]) => association);
  return names.length < 2
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/** GitHub caps a login at 39 characters; an App's `<slug>[bot]` runs longer. Generous, bounded. */
const RAW_TASK_AUTHOR_LOGIN_MAX = 100;
/** Longest documented association is `FIRST_TIME_CONTRIBUTOR` (22). */
const RAW_TASK_AUTHOR_ASSOCIATION_MAX = 64;

/**
 * Who wrote a raw task's text, stamped ONCE at the door that filed it — the stored
 * `raw_tasks.author_trust` jsonb (plan 101 slice 09, "Stamp provenance at intake").
 *
 * `trust` is {@link authorTrust}'s answer for a GitHub author; a door with no GitHub
 * author states its rung directly (`project` for a caller through our auth, `platform`
 * for our own machinery, `outside` for a tenant's connected tool, except a `sentry` source
 * pinned to one repo, which is `project` by owner ruling, #5651). `association` and
 * `login` are the raw GitHub facts the rung was decided from, kept so an audit can
 * re-derive it; `null` where the door had none. Both are REQUIRED and explicitly
 * nullable, never optional: a door states that it had no author, it does not omit one.
 *
 * `userId` is GitHub's numeric id of that login: a login can be renamed and re-registered, an id
 * cannot. OPTIONAL, unlike the two above: every row stored before it has none. READER ONLY for
 * now. No door writes it until this schema has rolled everywhere, because this shape is strict
 * and an older pod would end a row carrying the key `row_invalid`, never re-driven.
 *
 * Strict: a stored shape is a one-way door, so an unknown key is refused rather than
 * silently kept in the jsonb. The rung is NOT a pass: only the repository's own answer about
 * the login skips the intake screen (`IsRepoDeveloper`, #7785), and the screen runs on every
 * other author, whatever rung they hold.
 */
export const RawTaskAuthorTrustSchema = z
  .object({
    trust: z.enum(AUTHOR_TRUSTS),
    association: z.string().min(1).max(RAW_TASK_AUTHOR_ASSOCIATION_MAX).nullable(),
    login: z.string().min(1).max(RAW_TASK_AUTHOR_LOGIN_MAX).nullable(),
    userId: z.number().int().positive().nullable().optional(),
  })
  .strict();

/** The stored author-trust stamp on a raw task — see {@link RawTaskAuthorTrustSchema}. */
export type RawTaskAuthorTrust = z.infer<typeof RawTaskAuthorTrustSchema>;
